import { supabase } from '../config/supabase'
import { DEMO_USER_ID, isDemoMode } from '../utils/demoMode'
import { applySalonFilter, getSelectedSalonId, belongsToSalon } from '../utils/salonScope'
import {
  demoAddAppointment,
  demoCancelAppointment,
  demoGetAppointments,
  demoUpdateAppointment,
} from '../utils/demoStore'
import { distributeServicesForMultiDay } from '../utils/multiDayServices'

/**
 * Appointment Service
 * Handles appointment CRUD operations and availability checks
 * Campaign demo: writes go to local session store only (never Supabase).
 */

// Campaign demo opening hours — slots are generated locally so the demo never
// depends on schedule/segment tables the anonymous visitor may not read.
const DEMO_OPEN_MINUTES = 9 * 60
const DEMO_CLOSE_MINUTES = 19 * 60
const DEMO_STEP_MINUTES = 15

/** Parse YYYY-MM-DD as a local Date (avoids UTC weekday/off-by-one bugs). */
const parseLocalDateKey = (date) => {
  const key = String(date || '').slice(0, 10)
  const [y, m, d] = key.split('-').map(Number)
  if (y && m && d) return new Date(y, m - 1, d)
  const fallback = new Date(date)
  return Number.isNaN(fallback.getTime()) ? new Date() : fallback
}

/** Normalize to HH:MM:SS for Postgres TIME params. */
const toPgTime = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const h = Math.floor(value / 60) % 24
    const m = value % 60
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
  }
  const s = String(value || '').trim()
  if (/^\d{1,2}:\d{2}:\d{2}/.test(s)) {
    const [hh, mm, ss] = s.split(':')
    return `${String(hh).padStart(2, '0')}:${mm}:${String(ss).slice(0, 2).padStart(2, '0')}`
  }
  if (/^\d{1,2}:\d{2}/.test(s)) {
    const [hh, mm] = s.split(':')
    return `${String(hh).padStart(2, '0')}:${mm}:00`
  }
  return null
}

/**
 * Cross-user busy intervals via SECURITY DEFINER RPC.
 * Direct table SELECTs are RLS-scoped to the caller's own appointments only.
 */
const fetchBusyIntervalsByProfessionalRpc = async (professionalIds, date) => {
  const ids = (professionalIds || []).filter(Boolean).map(String)
  const byPro = {}
  ids.forEach((id) => {
    byPro[id] = []
  })
  if (!date || ids.length === 0) return byPro

  const { data, error } = await supabase.rpc('get_busy_intervals', {
    p_date: date,
    p_professional_ids: ids,
  })
  if (error) throw error

  ;(data || []).forEach((row) => {
    const pid = row.professional_id ? String(row.professional_id) : null
    if (!pid) return
    if (!byPro[pid]) byPro[pid] = []
    byPro[pid].push({
      professional_id: pid,
      start_time: row.start_time,
      end_time: row.end_time,
    })
  })
  return byPro
}

/** True if professional already has an overlapping active segment (any user). */
const checkProfessionalTimeConflictRpc = async (
  professionalId,
  date,
  start,
  end,
  excludeAppointmentId = null,
) => {
  const startTime = toPgTime(start)
  const endTime = toPgTime(end)
  if (!professionalId || !date || !startTime || !endTime) return false

  const { data, error } = await supabase.rpc('check_professional_time_conflict', {
    p_professional_id: professionalId,
    p_date: date,
    p_start_time: startTime,
    p_end_time: endTime,
    p_exclude_appointment_id: excludeAppointmentId || null,
  })
  if (error) {
    console.error('check_professional_time_conflict failed:', error)
    throw new Error('Could not verify professional availability. Please try again.')
  }
  return !!data
}

/** e.g. "Lucas, Emily, and Amelia are no longer available..." */
const formatProfessionalConflictMessage = (names = []) => {
  const unique = [...new Set((names || []).map((n) => String(n || '').trim()).filter(Boolean))]
  if (unique.length === 0) {
    return 'This time slot is no longer available. Please select another time.'
  }
  if (unique.length === 1) {
    return `${unique[0]} is no longer available at the selected time. Please select another time.`
  }
  if (unique.length === 2) {
    return `${unique[0]} and ${unique[1]} are no longer available at the selected time. Please select another time.`
  }
  const last = unique[unique.length - 1]
  return `${unique.slice(0, -1).join(', ')}, and ${last} are no longer available at the selected time. Please select another time.`
}

/**
 * Build fake-but-realistic time slots for demo mode.
 * Existing demo bookings on the same date are treated as busy.
 */
const buildDemoTimeSlots = (date, durationMinutes = 60, professionalIds = []) => {
  const duration = Math.max(parseInt(durationMinutes, 10) || 60, 15)

  const busy = demoGetAppointments(DEMO_USER_ID)
    .filter((apt) => apt.appointment_date === date && apt.status !== 'cancelled')
    .map((apt) => {
      const start = parseTimeToMinutes(String(apt.start_time || apt.appointment_time || '').substring(0, 5))
      if (start == null) return null
      return { start, end: start + (parseInt(apt.duration_minutes, 10) || 60) }
    })
    .filter(Boolean)

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const selected = new Date(date)
  selected.setHours(0, 0, 0, 0)
  const isToday = today.getTime() === selected.getTime()
  const minMinutes = isToday
    ? new Date().getHours() * 60 + new Date().getMinutes() + 30
    : 0

  const slots = []
  for (let start = DEMO_OPEN_MINUTES; start + duration <= DEMO_CLOSE_MINUTES; start += DEMO_STEP_MINUTES) {
    if (start < minMinutes) continue
    const end = start + duration
    const overlaps = busy.some((b) => start < b.end && end > b.start)
    if (overlaps) continue

    const timeString = `${minutesToTimeString(start)}:00`
    const endTimeString = `${minutesToTimeString(end)}:00`
    const startDisplay = formatTimeForDisplay(timeString)
    const endDisplay = formatTimeForDisplay(endTimeString)

    slots.push({
      time: timeString,
      endTime: endTimeString,
      display: `${startDisplay} - ${endDisplay}`,
      displayStart: startDisplay,
      displayEnd: endDisplay,
      available: true,
      availableProfessionals: professionalIds,
      availableCount: professionalIds.length,
    })
  }

  return { success: true, data: slots }
}

/**
 * Normalize professional ID to ensure it's a valid UUID
 * Handles numeric IDs and string IDs, converting them to UUID format if needed
 */
const normalizeProfessionalId = (prof) => {
  if (!prof) return null
  
  // If prof is already a string ID, use it directly
  if (typeof prof === 'string') {
    // If it's already a UUID (contains dashes), return as-is
    if (prof.includes('-')) return prof
    // If it's numeric-like string, might need mapping (but website uses UUIDs directly)
    return prof
  }
  
  // If prof is an object with an id property
  const rawId = prof.id || prof
  if (!rawId) return null
  
  if (typeof rawId === 'string') {
    // If already a UUID, use as-is
    if (rawId.includes('-')) return rawId
    return rawId
  }
  
  // If it's a number, might need mapping (but website should use UUIDs)
  if (typeof rawId === 'number') {
    return String(rawId)
  }
  
  return null
}

/**
 * Create a new appointment with segments for each service/professional
 * @param {Object} appointmentData - Appointment data
 * @returns {Promise<Object>} Created appointment with segments or error
 */
export const createAppointment = async (appointmentData) => {
  try {
    const {
      userId,
      profileId,
      professionalId,
      salonId,
      appointmentDate,
      startTime,
      durationMinutes,
      services,
      professionalData,
      totalAmount,
      paymentMethod = 'cash',
      paymentStatus = 'unpaid',
      status = 'confirmed',
      notes = null,
      walkinContact = null,
      excludeAppointmentId = null
    } = appointmentData

    const servicesArray = Array.isArray(services) ? services : [services]
    const usesPerServiceTimes = servicesArray.some(
      (s) => s?.scheduled_start_time || s?.scheduledStartTime
    )

    // Validate required fields (startTime can be derived from per-service schedules)
    if (!appointmentDate || !durationMinutes || !services || !totalAmount) {
      return {
        success: false,
        error: 'Missing required appointment fields'
      }
    }
    if (!startTime && !usesPerServiceTimes) {
      return {
        success: false,
        error: 'Missing required appointment fields'
      }
    }

    // Campaign demo: never write to the real database
    if (isDemoMode()) {
      console.log('🎭 DEMO MODE — appointment saved locally only (not in database)')
      return demoAddAppointment({
        ...appointmentData,
        userId: userId || DEMO_USER_ID,
        profileId: profileId || DEMO_USER_ID,
        paymentMethod,
        paymentStatus,
        status,
        notes: notes ? `${notes}\n[DEMO]` : '[DEMO — not saved to database]',
      })
    }

    // Parse start time to HH:MM format
    const parseTimeToHHMM = (timeStr) => {
      if (!timeStr) return null
      // Handle formats like "09:00:00" or "9:00 AM"
      if (timeStr.includes('AM') || timeStr.includes('PM')) {
        const [time, period] = timeStr.split(' ')
        const [hours, minutes] = time.split(':')
        let hour24 = parseInt(hours)
        if (period === 'PM' && hour24 !== 12) hour24 += 12
        if (period === 'AM' && hour24 === 12) hour24 = 0
        return `${String(hour24).padStart(2, '0')}:${minutes || '00'}`
      }
      // Already in HH:MM or HH:MM:SS format
      return timeStr.substring(0, 5)
    }

    const resolveServiceScheduledStart = (service) =>
      parseTimeToHHMM(service?.scheduled_start_time || service?.scheduledStartTime)
    const resolveServiceScheduledEnd = (service, duration) => {
      const explicit = parseTimeToHHMM(service?.scheduled_end_time || service?.scheduledEndTime)
      if (explicit) return explicit
      const start = resolveServiceScheduledStart(service)
      if (!start) return null
      const [h, m] = start.split(':').map(Number)
      const total = h * 60 + m + Math.max(0, duration || 0)
      return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
    }

    let startHHMM = parseTimeToHHMM(startTime)
    if (usesPerServiceTimes) {
      let earliest = null
      let earliestMin = Infinity
      servicesArray.forEach((service) => {
        const start = resolveServiceScheduledStart(service)
        const mins = start != null ? parseTimeToMinutes(start) : null
        if (mins != null && mins < earliestMin) {
          earliestMin = mins
          earliest = start
        }
      })
      if (earliest) startHHMM = earliest
    }

    if (!startHHMM) {
      return {
        success: false,
        error: 'Invalid start time format'
      }
    }

    // CRITICAL: Final availability check before creating appointment
    // This prevents double-booking when multiple users try to book the same slot
    // Parse time to minutes for comparison
    const slotStartMinutes = parseTimeToMinutes(startHHMM)
    const slotEndMinutes = usesPerServiceTimes
      ? Math.max(
          ...servicesArray.map((service) => {
            const end = resolveServiceScheduledEnd(service, serviceDurationMinutes(service))
            return end != null ? parseTimeToMinutes(end) : slotStartMinutes + durationMinutes
          }),
          slotStartMinutes + durationMinutes
        )
      : slotStartMinutes + durationMinutes
    
    // CHECK: Prevent user from booking at the same time (even with different professionals)
    if (userId) {
      let userApptQuery = supabase
        .from('appointments')
        .select('id, start_time, end_time, duration_minutes, professional_id')
        .eq('user_id', userId)
        .eq('appointment_date', appointmentDate)
        .in('status', ['pending', 'confirmed'])
      if (excludeAppointmentId) {
        userApptQuery = userApptQuery.neq('id', excludeAppointmentId)
      }
      const { data: userAppointments, error: userCheckError } = await userApptQuery
      
      if (!userCheckError && userAppointments && userAppointments.length > 0) {
        const serviceWindows = usesPerServiceTimes
          ? servicesArray
              .map((service) => {
                const start = resolveServiceScheduledStart(service)
                const end = resolveServiceScheduledEnd(service, serviceDurationMinutes(service))
                if (start == null || end == null) return null
                return {
                  start: parseTimeToMinutes(start),
                  end: parseTimeToMinutes(end),
                  label: start,
                }
              })
              .filter(Boolean)
          : [{ start: slotStartMinutes, end: slotEndMinutes, label: startHHMM }]

        const conflictingWindow = serviceWindows.find((window) =>
          userAppointments.some((apt) => {
            if (excludeAppointmentId && apt.id === excludeAppointmentId) return false
            const aptStart = parseTimeToMinutes(apt.start_time?.substring(0, 5) || apt.start_time)
            const aptEnd = apt.end_time
              ? parseTimeToMinutes(apt.end_time?.substring(0, 5) || apt.end_time)
              : (aptStart != null && apt.duration_minutes ? aptStart + apt.duration_minutes : null)
            if (aptStart == null || aptEnd == null) return false
            return window.start < aptEnd && window.end > aptStart
          })
        )

        if (conflictingWindow) {
          const formatTimeForDisplay = (time24) => {
            const [hours, minutes] = time24.split(':')
            const hour24 = parseInt(hours)
            const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
            const ampm = hour24 >= 12 ? 'PM' : 'AM'
            return `${hour12}:${minutes} ${ampm}`
          }

          const conflictTime = formatTimeForDisplay(conflictingWindow.label)
          return {
            success: false,
            error: `You already have a booking at ${conflictTime} on this date. Please choose a different time.`
          }
        }
      }
    }

    // Convert services early — multi-service carts are booked as SEQUENTIAL segments
    // (Pro A for service 1, then Pro B for service 2, …). Never require the primary
    // professional to be free for the entire cart duration.
    // (servicesArray already declared above)
    const isMultiSegmentBooking = servicesArray.length > 1

    if (professionalId && !isMultiSegmentBooking && !usesPerServiceTimes) {
      // Single-service: primary professional must be free for the full window
      try {
        const hasConflict = await checkProfessionalTimeConflictRpc(
          professionalId,
          appointmentDate,
          slotStartMinutes,
          slotEndMinutes,
          excludeAppointmentId,
        )
        if (hasConflict) {
          return {
            success: false,
            error: 'This time slot is no longer available. The professional is already booked at this time. Please select another time.'
          }
        }
      } catch (conflictErr) {
        return {
          success: false,
          error: conflictErr?.message || 'Could not verify professional availability. Please try again.'
        }
      }
    }
    
    // Per-service availability (chained OR explicit scheduled times)
    const uniqueProfessionalIds = [...new Set(
      servicesArray
        .map(service => resolveLinkedProfessional(service)?.id || service.professional_id)
        .filter(id => id && id !== 'any')
    )]
    
    if (usesPerServiceTimes) {
      for (let i = 0; i < servicesArray.length; i++) {
        for (let j = i + 1; j < servicesArray.length; j++) {
          const sA = servicesArray[i]
          const sB = servicesArray[j]
          const proA = resolveLinkedProfessional(sA)?.id || sA.professional_id || professionalId
          const proB = resolveLinkedProfessional(sB)?.id || sB.professional_id || professionalId
          const dateA = sA.scheduled_date || appointmentDate
          const dateB = sB.scheduled_date || appointmentDate
          if (dateA && dateB && String(dateA) !== String(dateB)) continue
          const startA = resolveServiceScheduledStart(sA)
          const endA = resolveServiceScheduledEnd(sA, serviceDurationMinutes(sA))
          const startB = resolveServiceScheduledStart(sB)
          const endB = resolveServiceScheduledEnd(sB, serviceDurationMinutes(sB))
          if (startA && endA && startB && endB) {
            const minA0 = parseTimeToMinutes(startA)
            const minA1 = parseTimeToMinutes(endA)
            const minB0 = parseTimeToMinutes(startB)
            const minB1 = parseTimeToMinutes(endB)
            if (minA0 != null && minA1 != null && minB0 != null && minB1 != null) {
              if (minA0 < minB1 && minB0 < minA1) {
                if (proA && proB && proA !== 'any' && String(proA) === String(proB)) {
                  return {
                    success: false,
                    error: `Times for "${sA.name}" and "${sB.name}" overlap for the same professional.`,
                  }
                }
                return {
                  success: false,
                  error: `Times for "${sA.name}" and "${sB.name}" overlap. Each service must have a separate time slot.`,
                }
              }
            }
          }
        }
      }
    }

    if (isMultiSegmentBooking || uniqueProfessionalIds.length > 0 || usesPerServiceTimes) {
      let currentStartMinutes = slotStartMinutes
      const conflictingNames = []
      
      for (let i = 0; i < servicesArray.length; i++) {
        const service = servicesArray[i]
        const linked = resolveLinkedProfessional(service)
        const serviceProfessionalId = linked?.id || service.professional_id || professionalId
        
        if (!serviceProfessionalId || serviceProfessionalId === 'any') {
          if (!usesPerServiceTimes) {
            currentStartMinutes += serviceDurationMinutes(service)
          }
          continue
        }
        
        const serviceDuration = serviceDurationMinutes(service)
        let serviceStartMinutes = currentStartMinutes
        let serviceEndMinutes = currentStartMinutes + serviceDuration
        if (usesPerServiceTimes) {
          const scheduledStart = resolveServiceScheduledStart(service)
          const scheduledEnd = resolveServiceScheduledEnd(service, serviceDuration)
          if (scheduledStart == null || scheduledEnd == null) {
            return {
              success: false,
              error: `Missing time for service "${service.name || 'service'}". Please select a time for each service.`,
            }
          }
          serviceStartMinutes = parseTimeToMinutes(scheduledStart)
          serviceEndMinutes = parseTimeToMinutes(scheduledEnd)
        }
        
        try {
          const hasConflict = await checkProfessionalTimeConflictRpc(
            serviceProfessionalId,
            appointmentDate,
            serviceStartMinutes,
            serviceEndMinutes,
            excludeAppointmentId,
          )
          if (hasConflict) {
            conflictingNames.push(
              linked?.name || service.professional?.name || service.name || 'Professional'
            )
          }
        } catch (conflictErr) {
          return {
            success: false,
            error: conflictErr?.message || 'Could not verify professional availability. Please try again.'
          }
        }
        
        if (!usesPerServiceTimes) {
          currentStartMinutes = serviceEndMinutes
        }
      }

      if (conflictingNames.length > 0) {
        return {
          success: false,
          error: formatProfessionalConflictMessage(conflictingNames),
        }
      }
    }

    // Build segments for each service with its professional
    // Note: startHHMM and servicesArray are already declared above, reuse them
    const segmentRows = []
    let [currentHH, currentMM] = startHHMM.split(':').map(Number)

    // Helper to add minutes and get next time
    const addMinutes = (hh, mm, add) => {
      const total = hh * 60 + mm + add
      const nh = Math.floor(total / 60) % 24
      const nm = total % 60
      return { hh: nh, mm: nm }
    }

    // Helper to format time (HH:MM format - PostgreSQL TIME accepts this)
    const toHHMM = (hh, mm) => `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`

    // Create segment for each service
    servicesArray.forEach((service) => {
      const linked = resolveLinkedProfessional(service)
      let serviceProfessionalId = linked?.id || service.professional_id || professionalId

      // If still "any", fall back to main professionalId (selected/assigned); if still missing, skip
      if (serviceProfessionalId === 'any') {
        serviceProfessionalId = professionalId
      }

      if (!serviceProfessionalId || serviceProfessionalId === 'any') {
        console.warn('⚠️ No valid professional assigned for service:', service.name)
        return
      }

      // Calculate service duration in minutes
      const serviceDuration = serviceDurationMinutes(service)

      let segmentStart
      let segmentEnd
      if (usesPerServiceTimes) {
        segmentStart = resolveServiceScheduledStart(service)
        segmentEnd = resolveServiceScheduledEnd(service, serviceDuration)
        if (!segmentStart || !segmentEnd) {
          console.warn('⚠️ Missing scheduled time for service:', service.name)
          return
        }
      } else {
        segmentStart = toHHMM(currentHH, currentMM)
        const after = addMinutes(currentHH, currentMM, serviceDuration)
        segmentEnd = toHHMM(after.hh, after.mm)
        currentHH = after.hh
        currentMM = after.mm
      }

      // Normalize professional ID to ensure it matches database format
      const normalizedProfessionalId = normalizeProfessionalId(serviceProfessionalId)
      
      if (!normalizedProfessionalId) {
        console.warn('⚠️ Could not normalize professional ID for service:', service.name, 'ID:', serviceProfessionalId)
        return
      }

      segmentRows.push({
        professional_id: normalizedProfessionalId,
        start_time: segmentStart,
        end_time: segmentEnd,
        duration_minutes: serviceDuration
      })
      
      console.log(`📝 Created segment for service "${service.name}":`, {
        professional_id: normalizedProfessionalId,
        original_id: serviceProfessionalId,
        start_time: segmentStart,
        end_time: segmentEnd
      })
    })

    // Parent end = last chronological segment end (gaps allowed)
    const parentEnd = segmentRows.length > 0
      ? segmentRows.reduce((latest, row) => {
          const latestMin = parseTimeToMinutes(latest)
          const rowMin = parseTimeToMinutes(row.end_time)
          if (rowMin == null) return latest
          if (latestMin == null || rowMin > latestMin) return row.end_time
          return latest
        }, segmentRows[0].end_time)
      : null

    // Prepare professional_data structure to match mobile app format
    // Mobile app stores: { primary, professionals, professionalIds, original }
    // Extract professional info from services array
    const serviceProfessionals = servicesArray
      .map(service => service.professional)
      .filter(prof => prof && prof.id && prof.id !== 'any')
    
    const professionalIds = [...new Set(
      segmentRows.map(s => s.professional_id).filter(Boolean)
    )]
    
    const primaryProfessionalId = professionalId || (segmentRows.length > 0 ? segmentRows[0].professional_id : null)
    const primaryProfessional = professionalData || 
      (primaryProfessionalId ? serviceProfessionals.find(p => p.id === primaryProfessionalId) : null) ||
      (serviceProfessionals.length > 0 ? serviceProfessionals[0] : null)
    
    let professionalDataFormatted = null
    if (primaryProfessional || serviceProfessionals.length > 0) {
      professionalDataFormatted = {
        primary: primaryProfessional || null,
        professionals: serviceProfessionals.length > 0 ? serviceProfessionals : (primaryProfessional ? [primaryProfessional] : []),
        professionalIds: professionalIds,
        original: professionalData || primaryProfessional || null
      }
    }

    // Prepare parent appointment data - matching mobile app structure exactly
    const appointmentPayload = {
      user_id: userId,
      profile_id: profileId || userId, // Fallback to userId if profileId not provided (like mobile app)
      professional_id: professionalId || (segmentRows.length > 0 ? segmentRows[0].professional_id : null),
      salon_id: salonId || null,
      appointment_date: appointmentDate,
      start_time: startHHMM, // Format: HH:MM (will be converted to TIME by database)
      appointment_time: startHHMM, // Same as start_time (for compatibility)
      end_time: parentEnd, // Format: HH:MM
      duration_minutes: durationMinutes,
      services: servicesArray, // JSONB array of services
      professional_data: professionalDataFormatted, // JSONB object matching mobile app format
      total_amount: parseFloat(totalAmount), // Ensure numeric type
      payment_method: paymentMethod || 'cash',
      payment_status: paymentStatus || 'unpaid',
      status: status || 'confirmed',
      notes: notes || null,
      walkin_contact: walkinContact || null,
      payment_intent_id: appointmentData?.paymentIntentId || null
    }

    console.log('📅 Creating appointment with segments:', {
      parent: appointmentPayload,
      segments: segmentRows,
      professionalId: professionalId,
      appointmentDate: appointmentDate,
      segmentRowsCount: segmentRows.length,
      segmentRowsDetails: segmentRows.map(s => ({
        professional_id: s.professional_id,
        start_time: s.start_time,
        end_time: s.end_time
      }))
    })

    // Insert parent appointment
    const { data, error } = await supabase
      .from('appointments')
      .insert([appointmentPayload])
      .select()
      .single()

    if (error) {
      console.error('❌ Error creating appointment:', error)
      return {
        success: false,
        error: error.message || 'Failed to create appointment'
      }
    }

    console.log('✅ Parent appointment created:', data.id)

    // CRITICAL: Create appointment segments for each service/professional
    // Segments are REQUIRED to prevent double booking - if segments fail, appointment must fail
    if (segmentRows.length > 0 && data.id) {
      const segmentPayload = segmentRows.map(segment => ({
        appointment_id: data.id,
        professional_id: segment.professional_id,
        start_time: segment.start_time,
        end_time: segment.end_time,
        duration_minutes: segment.duration_minutes,
        // Required so overlap trigger + busy RPC see this booking across accounts
        appointment_date: segment.appointment_date || appointmentDate,
      }))

      console.log('📝 Inserting appointment segments:', segmentPayload)

      const { data: segmentsData, error: segmentsError } = await supabase
        .from('appointment_segments')
        .insert(segmentPayload)
        .select()

      if (segmentsError) {
        console.error('❌ CRITICAL ERROR: Failed to create appointment segments:', segmentsError)
        console.error('❌ Segment payload:', segmentPayload)
        
        // CRITICAL: Delete the appointment if segments fail to prevent double booking
        try {
          await supabase
            .from('appointments')
            .delete()
            .eq('id', data.id)
          console.log('🗑️ Deleted appointment', data.id, 'due to segment creation failure')
        } catch (deleteError) {
          console.error('❌ Failed to delete appointment after segment failure:', deleteError)
        }
        
        // Check if error is due to double-booking (slot already taken)
        const errorMessage = segmentsError.message || ''
        const errorCode = segmentsError.code || ''
        const isDoubleBookingError = 
          errorMessage.includes('duplicate key value violates unique constraint') ||
          errorMessage.includes('idx_appointment_segments_unique_exact_slot') ||
          errorMessage.includes('idx_appointment_segments_exact_slot_unique') ||
          errorMessage.includes('time slot is already booked') ||
          errorMessage.includes('time slot is no longer available') ||
          errorMessage.includes('already booked') ||
          errorMessage.includes('prevent_overlapping_appointments') ||
          errorCode === '23505' // PostgreSQL unique constraint violation code
        
        if (isDoubleBookingError) {
          return {
            success: false,
            error: 'This time slot is no longer available. Another user has already booked this time. Please select a different time slot.'
          }
        }
        
        return {
          success: false,
          error: 'Failed to create appointment segments. This prevents double booking. Please try again. Error: ' + segmentsError.message
        }
      }

      if (!segmentsData || segmentsData.length === 0) {
        console.error('❌ CRITICAL ERROR: No segments were created despite no error')
        
        // Delete the appointment if no segments were created
        try {
          await supabase
            .from('appointments')
            .delete()
            .eq('id', data.id)
          console.log('🗑️ Deleted appointment', data.id, 'due to no segments created')
        } catch (deleteError) {
          console.error('❌ Failed to delete appointment after no segments:', deleteError)
        }
        
        return {
          success: false,
          error: 'Failed to create appointment segments. No segments were inserted. Please try again.'
        }
      }

      console.log('✅ Successfully created', segmentsData.length, 'appointment segments:', segmentsData)
      console.log('✅ Segment details for verification:', {
        appointmentId: data.id,
        appointmentDate: appointmentDate,
        segments: segmentsData.map(s => ({
          id: s.id,
          appointment_id: s.appointment_id,
          professional_id: s.professional_id,
          start_time: s.start_time,
          end_time: s.end_time,
          date: appointmentDate
        }))
      })
      console.log('✅ Segment details:', segmentsData.map(s => ({
        id: s.id,
        appointment_id: s.appointment_id,
        professional_id: s.professional_id,
        start_time: s.start_time,
        end_time: s.end_time,
        date: appointmentDate
      })))
      return {
        success: true,
        data: {
          ...data,
          segments: segmentsData
        }
      }
    }

    // If no segments were created but we have a professional, this is an error
    if (professionalId || (servicesArray && servicesArray.length > 0)) {
      console.error('❌ CRITICAL ERROR: No segments created but appointment has professional/services')
      console.error('❌ Professional ID:', professionalId)
      console.error('❌ Services:', servicesArray)
      console.error('❌ Segment rows:', segmentRows)
      
      // Delete the appointment if no segments were created
      try {
        await supabase
          .from('appointments')
          .delete()
          .eq('id', data.id)
        console.log('🗑️ Deleted appointment', data.id, 'due to missing segments')
      } catch (deleteError) {
        console.error('❌ Failed to delete appointment:', deleteError)
      }
      
      return {
        success: false,
        error: 'Failed to create appointment segments. No segments were generated. Please ensure services have assigned professionals.'
      }
    }

    // Only allow appointments without segments if there's no professional (shouldn't happen normally)
    console.warn('⚠️ Appointment created without segments (no professional assigned)')
    return {
      success: true,
      data: data,
      warning: 'Appointment created without segments - no professional assigned'
    }
  } catch (error) {
    console.error('❌ Exception creating appointment:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while creating appointment'
    }
  }
}

const toAppointmentDateLocal = (value) => {
  if (!value) return null
  const s = String(value).trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const extractDayStartHHMM = (daySel) => {
  const t = daySel?.time
  if (!t) return null
  const candidates = typeof t === 'string'
    ? [t]
    : [t.time, t.slotStart, t.start, t.slot]
  for (const raw of candidates) {
    if (!raw) continue
    const s = String(raw).trim()
    const startPart = s.split(/\s*[-–]\s*/)[0].trim()
    const m24 = startPart.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (m24 && !/[ap]m/i.test(startPart)) {
      return `${String(parseInt(m24[1], 10)).padStart(2, '0')}:${m24[2]}`
    }
    const m12 = startPart.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
    if (m12) {
      let h = parseInt(m12[1], 10)
      const min = m12[2]
      const ap = m12[3].toUpperCase()
      if (ap === 'PM' && h !== 12) h += 12
      if (ap === 'AM' && h === 12) h = 0
      return `${String(h).padStart(2, '0')}:${min}`
    }
  }
  return null
}

const serviceDurationMinutes = (s) => {
  if (typeof s?.duration_minutes === 'number' && s.duration_minutes > 0) {
    return Math.floor(s.duration_minutes)
  }
  const text = String(s?.duration || '')
  let minutes = 0
  const hrMatch = text.match(/(\d+(?:[.,]\d+)?)\s*hr/i)
  const minMatch = text.match(/(\d+)\s*min/i)
  if (hrMatch) minutes += Math.round(parseFloat(hrMatch[1].replace(',', '.')) * 60)
  if (minMatch) minutes += parseInt(minMatch[1], 10)
  if (!hrMatch && !minMatch) {
    const lone = text.match(/\d+/)
    if (lone) minutes += parseInt(lone[0], 10)
  }
  return minutes > 0 ? minutes : 60
}

const resolveLinkedProfessional = (s) =>
  s?.professional || s?.selectedProfessional || s?.assignedProfessional || s?.finalizedProfessional || null

export const createMultiDayAppointments = async (multiDayData) => {
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const {
    userId,
    profileId,
    salonId,
    daySelections,
    services,
    totalAmount,
    paymentMethod = 'cash',
    paymentStatus = 'unpaid',
    status = 'confirmed',
    notes = null,
    walkinContact = null,
    offerId = null,
    paymentIntentId = null
  } = multiDayData

  const safeOfferId = offerId && String(offerId).includes('-') ? offerId : null
  const safePaymentIntentId = paymentIntentId || null

  const totalDays = Math.max(1, (daySelections || []).length)
  const servicesForDays = distributeServicesForMultiDay(services || [], totalDays)

  if (isDemoMode()) {
    console.log('🎭 DEMO MODE — saving multi-day appointments locally')
    const groupBookingId = generateUUID()
    const created = []
    for (let i = 0; i < (daySelections || []).length; i++) {
      const daySel = daySelections[i]
      const dayNum = i + 1
      const dayServices = servicesForDays.filter(s => (s.day_number || 1) === dayNum)
      if (dayServices.length === 0) continue
      const dayDuration = dayServices.reduce((sum, s) => sum + serviceDurationMinutes(s), 0)
      const appointmentData = {
        userId: userId || DEMO_USER_ID,
        profileId: profileId || DEMO_USER_ID,
        professionalId: resolveLinkedProfessional(dayServices[0])?.id || 'any',
        salonId,
        appointmentDate: daySel.date,
        startTime: extractDayStartHHMM(daySel),
        durationMinutes: dayDuration,
        services: dayServices,
        totalAmount: created.length === 0 ? totalAmount : 0,
        paymentMethod,
        paymentStatus,
        status,
        notes: notes ? `${notes}\n[Day ${dayNum}]` : `[Day ${dayNum}]`,
        walkinContact,
        group_booking_id: groupBookingId,
        offer_id: safeOfferId
      }
      const res = demoAddAppointment(appointmentData)
      if (res.success) {
        created.push(res.data)
      }
    }
    return { success: true, data: created[0], allData: created, groupBookingId }
  }

  const created = []
  const rollbackCreatedDays = async () => {
    const ids = created.map((row) => row?.id).filter(Boolean)
    if (ids.length === 0) return
    try {
      await supabase.from('appointment_segments').delete().in('appointment_id', ids)
      await supabase.from('appointments').delete().in('id', ids)
      console.warn('🗑️ Rolled back partial multi-day booking:', ids)
    } catch (rollbackError) {
      console.error('❌ Failed to roll back partial multi-day booking:', rollbackError)
    }
  }

  try {
    const groupBookingId = generateUUID()

    for (let i = 0; i < (daySelections || []).length; i++) {
      const daySel = daySelections[i]
      const dayNum = i + 1
      const dayServices = servicesForDays.filter(s => (s.day_number || 1) === dayNum)
      const usesPerServiceTimes = dayServices.some(
        (s) => s?.scheduled_start_time || s?.scheduledStartTime
      )
      if (!daySel?.date || dayServices.length === 0) {
        continue
      }
      if (!daySel?.time && !usesPerServiceTimes) {
        continue
      }

      const formattedDate = toAppointmentDateLocal(daySel.date)
      let startHHMM = extractDayStartHHMM(daySel)
      if (usesPerServiceTimes) {
        let earliest = null
        let earliestMin = Infinity
        dayServices.forEach((s) => {
          const raw = s.scheduled_start_time || s.scheduledStartTime
          if (!raw) return
          const t = String(raw).substring(0, 5)
          const mins = parseTimeToMinutes(t)
          if (mins != null && mins < earliestMin) {
            earliestMin = mins
            earliest = t
          }
        })
        if (earliest) startHHMM = earliest
      }
      if (!formattedDate || !startHHMM) {
        await rollbackCreatedDays()
        return { success: false, error: `Invalid date or time for Day ${dayNum}` }
      }

      const dayDuration = dayServices.reduce((sum, s) => sum + serviceDurationMinutes(s), 0)
      const dayProfs = dayServices
        .map((s) => resolveLinkedProfessional(s))
        .filter((p) => p && p.id && p.id !== 'any')
      const primaryPro = normalizeProfessionalId(dayProfs[0]) || normalizeProfessionalId(dayServices[0]?.professional_id)

      const professionalDataFormatted = {
        primary: dayProfs[0] || null,
        professionals: dayProfs,
        professionalIds: [...new Set(dayProfs.map((p) => normalizeProfessionalId(p)).filter(Boolean))],
        original: dayProfs[0] || null
      }

      const appointmentPayload = {
        user_id: userId,
        profile_id: profileId || userId,
        professional_id: primaryPro,
        salon_id: salonId || null,
        appointment_date: formattedDate,
        start_time: startHHMM,
        appointment_time: startHHMM,
        end_time: minutesToTimeString(parseTimeToMinutes(startHHMM) + dayDuration),
        duration_minutes: dayDuration,
        services: dayServices.map(s => ({
          id: s.id,
          name: s.name,
          price: s.price,
          duration: s.duration,
          duration_minutes: serviceDurationMinutes(s),
          category_id: s.category_id,
          professional: resolveLinkedProfessional(s),
          professional_id: normalizeProfessionalId(resolveLinkedProfessional(s)) || null,
          day_number: s.day_number || dayNum,
          scheduled_start_time: s.scheduled_start_time || s.scheduledStartTime || null,
          scheduled_end_time: s.scheduled_end_time || s.scheduledEndTime || null,
          scheduled_date: s.scheduled_date || s.scheduledDate || null,
        })),
        professional_data: professionalDataFormatted,
        total_amount: created.length === 0 ? parseFloat(totalAmount || 0) : 0,
        payment_method: paymentMethod || 'cash',
        payment_status: paymentStatus || 'unpaid',
        status: status || 'confirmed',
        notes: notes
          ? `${notes}\n[Day ${dayNum}]`
          : `[Day ${dayNum}]`,
        walkin_contact: walkinContact || null,
        group_booking_id: groupBookingId,
        offer_id: safeOfferId,
        ...(created.length === 0 && safePaymentIntentId
          ? { payment_intent_id: safePaymentIntentId }
          : {}),
      }

      const { data, error } = await supabase
        .from('appointments')
        .insert([appointmentPayload])
        .select()
        .single()

      if (error) {
        console.error(`❌ Error creating day ${dayNum} appointment:`, error)
        await rollbackCreatedDays()
        return { success: false, error: error.message || `Failed to create appointment for Day ${dayNum}` }
      }

      created.push(data)

      const segmentRows = []
      const seenSlotKeys = new Set()
      let currentMinutes = parseTimeToMinutes(startHHMM)

      for (let j = 0; j < dayServices.length; j++) {
        const s = dayServices[j]
        const proId = normalizeProfessionalId(resolveLinkedProfessional(s)) || primaryPro
        if (!proId || proId === 'any') continue

        const duration = serviceDurationMinutes(s)
        let segmentStart
        let segmentEnd
        if (usesPerServiceTimes) {
          const rawStart = s.scheduled_start_time || s.scheduledStartTime
          const rawEnd = s.scheduled_end_time || s.scheduledEndTime
          segmentStart = rawStart ? String(rawStart).substring(0, 5) : null
          segmentEnd = rawEnd
            ? String(rawEnd).substring(0, 5)
            : (segmentStart
              ? minutesToTimeString(parseTimeToMinutes(segmentStart) + duration)
              : null)
          if (!segmentStart || !segmentEnd) {
            await rollbackCreatedDays()
            return {
              success: false,
              error: `Day ${dayNum}: missing time for "${s.name || 'service'}". Please select a time for each service.`,
            }
          }
        } else {
          segmentStart = minutesToTimeString(currentMinutes)
          segmentEnd = minutesToTimeString(currentMinutes + duration)
          currentMinutes += duration
        }
        const slotKey = `${proId}|${formattedDate}|${segmentStart}|${segmentEnd}`

        // Same pro cannot own the exact same slot twice (DB unique index)
        if (seenSlotKeys.has(slotKey)) {
          await rollbackCreatedDays()
          return {
            success: false,
            error: `Day ${dayNum}: "${s.name}" was assigned the same professional and time as another service. Please reselect professionals or times and try again.`,
          }
        }
        seenSlotKeys.add(slotKey)

        segmentRows.push({
          appointment_id: data.id,
          professional_id: proId,
          start_time: segmentStart,
          end_time: segmentEnd,
          duration_minutes: duration,
          appointment_date: formattedDate
        })
      }

      if (usesPerServiceTimes && segmentRows.length > 0) {
        const latestEnd = segmentRows.reduce((latest, row) => {
          const a = parseTimeToMinutes(latest)
          const b = parseTimeToMinutes(row.end_time)
          return b != null && (a == null || b > a) ? row.end_time : latest
        }, segmentRows[0].end_time)
        await supabase
          .from('appointments')
          .update({ end_time: latestEnd })
          .eq('id', data.id)
      }

      if (segmentRows.length === 0) {
        await rollbackCreatedDays()
        return { success: false, error: `Failed to create segments for Day ${dayNum}. Please select a professional for each service.` }
      }

      // Block overlapping professional times across accounts before insert
      for (const row of segmentRows) {
        try {
          const hasConflict = await checkProfessionalTimeConflictRpc(
            row.professional_id,
            formattedDate,
            row.start_time,
            row.end_time,
            null,
          )
          if (hasConflict) {
            await rollbackCreatedDays()
            return {
              success: false,
              error: `Day ${dayNum}: a professional is no longer available at the selected time. Please choose another time.`,
            }
          }
        } catch (conflictErr) {
          await rollbackCreatedDays()
          return {
            success: false,
            error: conflictErr?.message || `Day ${dayNum}: could not verify professional availability.`,
          }
        }
      }

      const { error: segError } = await supabase
        .from('appointment_segments')
        .insert(segmentRows)

      if (segError) {
        console.error(`❌ Error creating day ${dayNum} segments:`, segError)
        await rollbackCreatedDays()
        const msg = String(segError.message || '')
        const isExactSlotDup =
          msg.includes('idx_appointment_segments_exact_slot_unique') ||
          msg.includes('idx_appointment_segments_unique_exact_slot') ||
          msg.includes('idx_appointment_segments_exact_unique') ||
          msg.includes('duplicate key value violates unique constraint')
        return {
          success: false,
          error: isExactSlotDup
            ? 'This time slot is no longer available for one of the professionals. Please choose a different time and try again.'
            : (segError.message || `Failed to create segments for Day ${dayNum}`),
        }
      }
    }

    if (created.length === 0) {
      return { success: false, error: 'No package days were ready to book. Please select a date and time for each day.' }
    }

    return { success: true, data: created[0], allData: created, groupBookingId }
  } catch (error) {
    console.error('❌ Exception creating multi-day package:', error)
    await rollbackCreatedDays()
    return { success: false, error: 'An unexpected error occurred while creating multi-day booking' }
  }
}


/**
 * Create appointment segments
 * @param {Object} segmentData - Segment data
 * @returns {Promise<Object>} Created segments or error
 */
export const createAppointmentSegments = async (segmentData) => {
  try {
    const { appointmentId, professionalId, startTime, durationMinutes } = segmentData

    // Calculate end time
    const start = new Date(`2000-01-01T${startTime}`)
    const end = new Date(start.getTime() + durationMinutes * 60000)
    const endTime = end.toTimeString().slice(0, 5) // HH:MM format

    const segmentPayload = {
      appointment_id: appointmentId,
      professional_id: professionalId,
      start_time: startTime,
      end_time: endTime,
      duration_minutes: durationMinutes,
      appointment_date: segmentData.appointmentDate || segmentData.appointment_date || null,
    }

    const { data, error } = await supabase
      .from('appointment_segments')
      .insert([segmentPayload])
      .select()
      .single()

    if (error) {
      console.error('❌ Error creating appointment segment:', error)
      return { success: false, error: error.message }
    }

    return { success: true, data }
  } catch (error) {
    console.error('❌ Exception creating appointment segment:', error)
    return { success: false, error: 'Failed to create appointment segment' }
  }
}

/**
 * Get user's appointments
 * @param {string} userId - User ID
 * @param {Object} options - Query options
 * @returns {Promise<Object>} Appointments or error
 */
export const getUserAppointments = async (userId, options = {}) => {
  try {
    const {
      status = null,
      limit = null,
      orderBy = 'appointment_date',
      orderDirection = 'asc',
      upcomingOnly = false,
      salonId: salonIdOption,
    } = options
    const salonId = salonIdOption || getSelectedSalonId()

    if (isDemoMode()) {
      let data = demoGetAppointments(userId || DEMO_USER_ID)
      if (salonId) {
        data = data.filter((a) => belongsToSalon(a, salonId))
      }
      if (status) data = data.filter((a) => a.status === status)
      if (upcomingOnly) {
        const today = new Date().toISOString().split('T')[0]
        data = data.filter(
          (a) => a.appointment_date >= today && ['pending', 'confirmed'].includes(a.status)
        )
      }
      data = [...data].sort((a, b) => {
        const av = a[orderBy] || ''
        const bv = b[orderBy] || ''
        return orderDirection === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1)
      })
      if (limit) data = data.slice(0, limit)
      return { success: true, data }
    }

    let query = supabase
      .from('appointments')
      .select(`
        *,
        professional:professionals(id, name, role, image_url),
        salon:salons(id, name)
      `)
      .eq('user_id', userId)
    query = applySalonFilter(query, { salonId })

    // Filter by status
    if (status) {
      query = query.eq('status', status)
    }

    // Filter upcoming appointments
    if (upcomingOnly) {
      const today = new Date().toISOString().split('T')[0]
      query = query.gte('appointment_date', today)
      query = query.in('status', ['pending', 'confirmed'])
    }

    // Order by
    query = query.order(orderBy, { ascending: orderDirection === 'asc' })

    // Limit
    if (limit) {
      query = query.limit(limit)
    }

    const { data, error } = await query

    if (error) {
      console.error('❌ Error fetching appointments:', error)
      return {
        success: false,
        error: error.message || 'Failed to fetch appointments'
      }
    }

    const filtered = (data || []).filter((apt) => belongsToSalon(apt, salonId))
    const offerIds = [...new Set(filtered.map((a) => a.offer_id).filter(Boolean))]
    let offerMap = {}
    if (offerIds.length > 0) {
      try {
        let { data: offers, error: offerErr } = await supabase
          .from('hero_offers')
          .select('id, title, total_days, starts_at, ends_at, allowed_payment_methods, reschedule_allowed, cancel_allowed, reschedule_policy_override, cancel_policy_override, payment_methods, allow_reschedule, allow_cancel, override_reschedule_policy, override_cancel_policy')
          .in('id', offerIds)
        if (offerErr && /payment_methods|allowed_payment_methods|allow_reschedule|allow_cancel|reschedule_allowed|cancel_allowed|override_|policy_override/i.test(offerErr.message || '')) {
          ;({ data: offers, error: offerErr } = await supabase
            .from('hero_offers')
            .select('id, title, total_days, starts_at, ends_at')
            .in('id', offerIds))
        }
        if (offerErr) throw offerErr
        ;(offers || []).forEach((o) => {
          offerMap[o.id] = o
        })
      } catch (e) {
        console.warn('Could not load offer titles for appointments:', e)
      }
    }

    return {
      success: true,
      data: filtered.map((apt) => ({
        ...apt,
        hero_offers: apt.offer_id ? offerMap[apt.offer_id] || null : null,
      }))
    }
  } catch (error) {
    console.error('❌ Exception fetching appointments:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while fetching appointments'
    }
  }
}

/**
 * Load the logged-in user's busy intervals for a date so those times
 * are not offered again (same check createAppointment uses on submit).
 */
const fetchUserBusyIntervals = async (userId, date, excludeAppointmentId = null) => {
  if (!userId || !date) return []
  try {
    let query = supabase
      .from('appointments')
      .select('id, start_time, end_time, duration_minutes')
      .eq('user_id', userId)
      .eq('appointment_date', date)
      .in('status', ['pending', 'confirmed', 'upcoming'])
    if (excludeAppointmentId) {
      query = query.neq('id', excludeAppointmentId)
    }
    const { data, error } = await query
    if (error || !data) return []
    return data
      .map((apt) => {
        const start = parseTimeToMinutes(String(apt.start_time || '').substring(0, 5))
        if (start == null) return null
        const end = apt.end_time
          ? parseTimeToMinutes(String(apt.end_time).substring(0, 5))
          : start + (parseInt(apt.duration_minutes, 10) || 60)
        if (end == null) return null
        return { start, end }
      })
      .filter(Boolean)
  } catch (e) {
    console.warn('Could not load user busy intervals:', e)
    return []
  }
}

const filterSlotsAgainstUserBusy = (slots, userBusy, durationMinutes = 60) => {
  if (!Array.isArray(slots) || slots.length === 0 || !userBusy?.length) return slots
  return slots.filter((slot) => {
    const start = parseTimeToMinutes(String(slot.time || '').substring(0, 5))
    if (start == null) return true
    const end = slot.endTime
      ? parseTimeToMinutes(String(slot.endTime).substring(0, 5))
      : start + (parseInt(durationMinutes, 10) || 60)
    return !userBusy.some((busy) => start < busy.end && end > busy.start)
  })
}

const applyUserBusyFilter = async (result, userId, date, excludeAppointmentId, durationMinutes) => {
  if (!result?.success || !result.data || !userId) return result
  const userBusy = await fetchUserBusyIntervals(userId, date, excludeAppointmentId)
  return {
    ...result,
    data: filterSlotsAgainstUserBusy(result.data, userBusy, durationMinutes)
  }
}

/**
 * Get available time slots for a professional on a specific date
 * @param {string} professionalId - Professional ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} durationMinutes - Duration in minutes
 * @param {Object} [options]
 * @param {string} [options.userId] - Hide slots that overlap this user's existing bookings
 * @param {string} [options.excludeAppointmentId] - Appointment to ignore (reschedule)
 * @returns {Promise<Object>} Available time slots
 */
export const getAvailableTimeSlots = async (professionalId, date, durationMinutes = 60, options = {}) => {
  try {
    if (isDemoMode()) {
      return buildDemoTimeSlots(date, durationMinutes, professionalId ? [professionalId] : [])
    }

    // Fetch professional schedule
    const schedules = await fetchProfessionalSchedules([professionalId])
    const schedule = schedules[professionalId]
    
    // Check if professional has a schedule entry
    if (!schedule || typeof schedule !== 'object' || Object.keys(schedule).length === 0) {
      console.log(`⚠️ Professional ${professionalId} has no schedule entry - returning no slots`)
      return {
        success: true,
        data: [] // No schedule = professional is off
      }
    }
    
    // Check if schedule is inactive
    if (schedule.is_active === false || schedule.is_active === null) {
      console.log(`⚠️ Professional ${professionalId} is inactive - returning no slots`)
      return {
        success: true,
        data: [] // Inactive = professional is off
      }
    }
    
    // Check if professional is working on this specific day (parse as local date, not UTC)
    const dateObj = parseLocalDateKey(date)
    const weekdayIdx = dateObj.getDay()
    const weekdayKey = String(weekdayIdx)
    const exceptions = schedule.exceptions || schedule.Exceptions || {}
    const rawDays = schedule.days || schedule.Days || {}
    
    // Check if days object is empty
    if (!rawDays || typeof rawDays !== 'object' || Object.keys(rawDays).length === 0) {
      console.log(`⚠️ Professional ${professionalId} has no days configured - returning no slots`)
      return {
        success: true,
        data: [] // No working days = professional is off
      }
    }
    
    // Check exception first (date-specific override)
    const exceptionDay = exceptions[date]
    const hasException = exceptionDay && typeof exceptionDay === 'object' && (exceptionDay.open || exceptionDay.start_time)
    
    // Check regular weekly schedule
    const weekdaySchedule = rawDays[weekdayKey]
    const hasWeekdaySchedule = weekdaySchedule && typeof weekdaySchedule === 'object' && (weekdaySchedule.open || weekdaySchedule.start_time)
    
    // If no schedule for this day, professional is off
    if (!hasException && !hasWeekdaySchedule) {
      console.log(`⚠️ Professional ${professionalId} has no working hours for ${date} (weekday ${weekdayIdx}) - returning no slots`)
      return {
        success: true,
        data: [] // No schedule for this day = professional is off
      }
    }

    // Get busy intervals from appointment_segments (more accurate for multi-professional bookings)
    // Query segments for this professional on this date
    // IMPORTANT: Only include appointments that are NOT cancelled/completed/no_show/missed
    // Exclude: cancelled, completed, no_show, missed
    // Include: pending, confirmed, upcoming (any active appointment)
    console.log(`🔍 Fetching busy intervals for professional ${professionalId} on ${date}`)
    console.log(`🔍 Query parameters:`, {
      professionalId: professionalId,
      date: date,
      dateType: typeof date,
      professionalIdType: typeof professionalId
    })
    
    // Try using RPC function first (same as mobile app) for better reliability
    let segments = null
    
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_busy_intervals', {
        p_date: date,
        p_professional_ids: [professionalId]
      })
      
      if (!rpcError && rpcData) {
        // Convert RPC response to match our format
        segments = rpcData.map(r => ({
          start_time: r.start_time,
          end_time: r.end_time,
          professional_id: r.professional_id,
          appointment_id: null // RPC doesn't return this
        }))
        console.log(`✅ Used RPC function - Found ${segments.length} busy intervals`)
      } else {
        throw rpcError || new Error('RPC returned no data')
      }
    } catch (rpcErr) {
      // Do not fall back to RLS-scoped SELECTs — they hide other users' bookings
      // and make already-booked multi-service / package slots appear free.
      console.error('⚠️ get_busy_intervals failed (fail closed):', rpcErr?.message || rpcErr)
      return {
        success: false,
        error: rpcErr?.message || 'Could not load availability. Please try again.'
      }
    }

    let busyIntervals = []
    
    {
      // Convert segments to busy intervals format
      // Segments already have start_time and end_time, use them directly
      console.log(`📊 Found ${segments?.length || 0} segments for professional ${professionalId} on ${date}`)
      console.log(`📊 Segments data:`, segments)
      
      busyIntervals = (segments || []).map(seg => {
        const start = seg.start_time?.substring(0, 5) || seg.start_time
        const end = seg.end_time?.substring(0, 5) || seg.end_time
        return { start, end }
      }).filter(b => b.start && b.end && b.start !== b.end)
      
      console.log(`📊 Processed ${busyIntervals.length} busy intervals for professional ${professionalId} on ${date}:`, busyIntervals)
    }

    // Compute free intervals based on schedule
    const freeIntervals = computeFreeIntervalsForProfessional(date, schedule, busyIntervals)

    // Check if selected date is today
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDateObj = parseLocalDateKey(date)
    selectedDateObj.setHours(0, 0, 0, 0)
    const isToday = today.getTime() === selectedDateObj.getTime()
    
    // Calculate minimum time (30 minutes from now) if it's today
    let minTimeMinutes = 0
    if (isToday) {
      const now = new Date()
      const currentHours = now.getHours()
      const currentMinutes = now.getMinutes()
      minTimeMinutes = (currentHours * 60) + currentMinutes + 30 // 30 minutes buffer
    }

    // Generate time slots from free intervals
    const allSlots = []
    const stepMinutes = 15 // Generate slots every 15 minutes

    freeIntervals.forEach(interval => {
      for (let totalMinutes = interval.start; totalMinutes + durationMinutes <= interval.end; totalMinutes += stepMinutes) {
        // Filter out slots that are in the past (if today)
        if (isToday && totalMinutes < minTimeMinutes) {
          continue
        }
        
        const timeString = `${minutesToTimeString(totalMinutes)}:00`
        const endTotalMinutes = totalMinutes + durationMinutes
        const endTimeString = `${minutesToTimeString(endTotalMinutes)}:00`
        
        // Format for display: "9:00 AM - 9:45 AM"
        const startDisplay = formatTimeForDisplay(timeString)
        const endDisplay = formatTimeForDisplay(endTimeString)
        
        allSlots.push({
          time: timeString,
          endTime: endTimeString,
          display: `${startDisplay} - ${endDisplay}`,
          displayStart: startDisplay,
          displayEnd: endDisplay,
          available: true
        })
      }
    })

    return applyUserBusyFilter(
      { success: true, data: allSlots },
      options?.userId,
      date,
      options?.excludeAppointmentId,
      durationMinutes
    )
  } catch (error) {
    console.error('❌ Exception getting available time slots:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while checking availability'
    }
  }
}

/**
 * Get available time slots across ALL professionals (for "any specialist" selection)
 * Returns slots that are available for at least one professional
 * @param {Array} professionalIds - Array of professional IDs
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} durationMinutes - Duration in minutes
 * @returns {Promise<Object>} Available time slots with professional availability info
 */
export const getAvailableTimeSlotsForAnySpecialist = async (professionalIds, date, durationMinutes = 60, options = {}) => {
  try {
    if (isDemoMode()) {
      return buildDemoTimeSlots(date, durationMinutes, professionalIds || [])
    }

    if (!professionalIds || professionalIds.length === 0) {
      return {
        success: false,
        error: 'No professionals provided'
      }
    }

    // Fetch professional schedules
    const schedules = await fetchProfessionalSchedules(professionalIds)

    // Cross-user busy times (SECURITY DEFINER RPC) — direct segment SELECT is RLS-blind
    let appointmentsByProfessional = {}
    try {
      appointmentsByProfessional = await fetchBusyIntervalsByProfessionalRpc(professionalIds, date)
    } catch (busyErr) {
      console.error('Failed to load busy intervals via RPC:', busyErr)
      return {
        success: false,
        error: busyErr?.message || 'Could not load availability. Please try again.'
      }
    }

    // Check if selected date is today
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDateObj = parseLocalDateKey(date)
    selectedDateObj.setHours(0, 0, 0, 0)
    const isToday = today.getTime() === selectedDateObj.getTime()
    
    // Calculate minimum time (30 minutes from now) if it's today
    let minTimeMinutes = 0
    if (isToday) {
      const now = new Date()
      const currentHours = now.getHours()
      const currentMinutes = now.getMinutes()
      minTimeMinutes = (currentHours * 60) + currentMinutes + 30 // 30 minutes buffer
    }

    // Collect all available slots from all professionals
    const slotMap = new Map() // Use Map to track slots and their available professionals
    const stepMinutes = 15

    professionalIds.forEach(profId => {
      const schedule = schedules[profId] || schedules[String(profId).toLowerCase()]
      
      // If professional has no schedule entry, they're off - skip
      if (!schedule || typeof schedule !== 'object' || Object.keys(schedule).length === 0) {
        console.log(`⚠️ Professional ${profId} not found in schedules - skipping`)
        return // Skip this professional
      }
      
      const professionalAppointments =
        appointmentsByProfessional[profId] ||
        appointmentsByProfessional[String(profId)] ||
        []
      
      // Convert appointments/segments to busy intervals
      const busyIntervals = professionalAppointments.map(apt => ({
        start: apt.start_time?.substring(0, 5) || apt.start_time,
        end: apt.end_time?.substring(0, 5) || apt.end_time ||
             (apt.start_time ? minutesToTimeString(parseTimeToMinutes(apt.start_time?.substring(0, 5)) + (apt.duration_minutes || 60)) : null)
      })).filter(b => b.start && b.end)
      
      // Check if professional is working on this day before computing free intervals
      const dateObj = parseLocalDateKey(date)
      const weekdayIdx = dateObj.getDay()
      const weekdayKey = String(weekdayIdx)
      const exceptions = schedule.exceptions || schedule.Exceptions || {}
      const rawDays = schedule.days || schedule.Days || {}
      
      // Check if schedule is inactive
      if (schedule.is_active === false || schedule.is_active === null) {
        console.log(`⚠️ Professional ${profId} is inactive - skipping`)
        return // Skip this professional
      }
      
      // Check if days object is empty
      if (!rawDays || typeof rawDays !== 'object' || Object.keys(rawDays).length === 0) {
        console.log(`⚠️ Professional ${profId} has no days configured - skipping`)
        return // Skip this professional
      }
      
      const exceptionDay = exceptions[date]
      const hasRawException = exceptionDay && typeof exceptionDay === 'object'
      const weekdaySchedule = rawDays[weekdayKey]
      const hasWeekdaySchedule = weekdaySchedule && typeof weekdaySchedule === 'object' && (weekdaySchedule.open || weekdaySchedule.start_time)

      // Treat explicit "off" exception (open === false and no custom hours) as closed day
      const isClosedException =
        hasRawException &&
        exceptionDay.open === false &&
        !exceptionDay.start_time &&
        !exceptionDay.close &&
        !exceptionDay.end_time
      
      // If professional is off (closed exception OR no schedule for this day), skip adding slots
      if (isClosedException || (!hasRawException && !hasWeekdaySchedule)) {
        console.log(`⚠️ Professional ${profId} is off for ${date} (weekday ${weekdayIdx}) - skipping`)
        return // Skip this professional - they're off
      }

      // Compute free intervals for this professional
      const freeIntervals = computeFreeIntervalsForProfessional(date, schedule, busyIntervals)

      // Generate slots from free intervals
      freeIntervals.forEach(interval => {
        for (let totalMinutes = interval.start; totalMinutes + durationMinutes <= interval.end; totalMinutes += stepMinutes) {
          if (isToday && totalMinutes < minTimeMinutes) {
            continue
          }
          
          const timeString = `${minutesToTimeString(totalMinutes)}:00`
          const endTotalMinutes = totalMinutes + durationMinutes
          const endTimeString = `${minutesToTimeString(endTotalMinutes)}:00`
          
          const startDisplay = formatTimeForDisplay(timeString)
          const endDisplay = formatTimeForDisplay(endTimeString)
          const slotKey = timeString
          
          if (!slotMap.has(slotKey)) {
            slotMap.set(slotKey, {
              time: timeString,
              endTime: endTimeString,
              display: `${startDisplay} - ${endDisplay}`,
              displayStart: startDisplay,
              displayEnd: endDisplay,
              available: true,
              availableProfessionals: [],
              availableCount: 0
            })
          }
          
          const slot = slotMap.get(slotKey)
          slot.availableProfessionals.push(profId)
          slot.availableCount = slot.availableProfessionals.length
        }
      })
    })

    const allSlots = Array.from(slotMap.values()).sort((a, b) =>
      String(a.time || '').localeCompare(String(b.time || ''))
    )

    return applyUserBusyFilter(
      { success: true, data: allSlots },
      options?.userId,
      date,
      options?.excludeAppointmentId,
      durationMinutes
    )
  } catch (error) {
    console.error('❌ Exception getting available time slots for any specialist:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while checking availability'
    }
  }
}

/**
 * Sequential multi-service "Any professional" availability.
 * Each service step can be covered by ANY eligible pro for that service
 * (not a single locked first ID). Used when services need different specialists.
 *
 * @param {Array<{ professionalIds: string[], durationMinutes: number }>} serviceCandidates
 * @param {string} date YYYY-MM-DD
 * @param {Object} options
 */
export const getAvailableTimeSlotsForSequentialAny = async (
  serviceCandidates = [],
  date,
  options = {}
) => {
  try {
    if (isDemoMode()) {
      const total = (serviceCandidates || []).reduce(
        (sum, s) => sum + (Number(s.durationMinutes) || 0),
        0
      )
      const allIds = [
        ...new Set(
          (serviceCandidates || []).flatMap((s) => s.professionalIds || []).filter(Boolean)
        ),
      ]
      return buildDemoTimeSlots(date, total || 60, allIds)
    }

    const steps = (serviceCandidates || [])
      .map((entry) => ({
        professionalIds: [...new Set((entry.professionalIds || []).map(String).filter(Boolean))],
        durationMinutes: Number(entry.durationMinutes) || 0,
      }))
      .filter((entry) => entry.professionalIds.length > 0 && entry.durationMinutes > 0)

    if (steps.length === 0) {
      return { success: false, error: 'No professionals provided' }
    }

    // Single service → classic union of eligible pros
    if (steps.length === 1) {
      return getAvailableTimeSlotsForAnySpecialist(
        steps[0].professionalIds,
        date,
        steps[0].durationMinutes,
        options
      )
    }

    const allProfessionalIds = [
      ...new Set(steps.flatMap((step) => step.professionalIds)),
    ]
    const schedules = await fetchProfessionalSchedules(allProfessionalIds)

    let appointmentsByProfessional = {}
    try {
      appointmentsByProfessional = await fetchBusyIntervalsByProfessionalRpc(
        allProfessionalIds,
        date
      )
    } catch (busyErr) {
      console.error('Failed to load busy intervals via RPC:', busyErr)
      return {
        success: false,
        error: busyErr?.message || 'Could not load availability. Please try again.',
      }
    }

    const freeById = {}
    allProfessionalIds.forEach((profId) => {
      const schedule = schedules[profId] || schedules[String(profId).toLowerCase()] || {}
      const busyRows =
        appointmentsByProfessional[profId] ||
        appointmentsByProfessional[String(profId)] ||
        []
      const busyIntervals = busyRows
        .map((apt) => ({
          start: String(apt.start_time || '').substring(0, 5),
          end: String(apt.end_time || '').substring(0, 5),
        }))
        .filter((b) => b.start && b.end)
      freeById[String(profId)] = computeFreeIntervalsForProfessional(
        date,
        schedule,
        busyIntervals,
        true
      )
    })

    const covers = (intervals, start, end) =>
      (intervals || []).some((iv) => iv.start <= start && iv.end >= end)

    const totalDur = steps.reduce((sum, s) => sum + s.durationMinutes, 0)
    let windowStart = Infinity
    let windowEnd = -Infinity
    Object.values(freeById).forEach((intervals) => {
      ;(intervals || []).forEach((iv) => {
        if (iv.start < windowStart) windowStart = iv.start
        if (iv.end > windowEnd) windowEnd = iv.end
      })
    })
    if (!Number.isFinite(windowStart) || !Number.isFinite(windowEnd) || totalDur <= 0) {
      return { success: true, data: [] }
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDateObj = parseLocalDateKey(date)
    selectedDateObj.setHours(0, 0, 0, 0)
    const isToday = today.getTime() === selectedDateObj.getTime()
    let minTimeMinutes = 0
    if (isToday) {
      const now = new Date()
      minTimeMinutes = now.getHours() * 60 + now.getMinutes() + 30
    }

    const stepMinutes = 15
    const allSlots = []

    for (let start = windowStart; start + totalDur <= windowEnd; start += stepMinutes) {
      if (isToday && start < minTimeMinutes) continue

      let cursor = start
      let ok = true
      const usedInSlot = new Set()
      const coveringPros = []

      for (const step of steps) {
        const dur = step.durationMinutes
        const pick = step.professionalIds.find((pid) => {
          if (usedInSlot.has(String(pid))) {
            // Same pro can only cover sequential segments if still free for this window
            // (already in usedInSlot means assigned earlier in this cart — allow if free)
          }
          return covers(freeById[String(pid)], cursor, cursor + dur)
        })
        if (!pick) {
          ok = false
          break
        }
        coveringPros.push(pick)
        usedInSlot.add(String(pick))
        cursor += dur
      }
      if (!ok) continue

      const timeString = `${minutesToTimeString(start)}:00`
      const endTimeString = `${minutesToTimeString(start + totalDur)}:00`
      const startDisplay = formatTimeForDisplay(timeString)
      const endDisplay = formatTimeForDisplay(endTimeString)
      allSlots.push({
        time: timeString,
        endTime: endTimeString,
        display: `${startDisplay} - ${endDisplay}`,
        displayStart: startDisplay,
        displayEnd: endDisplay,
        available: true,
        availableProfessionals: coveringPros,
        availableCount: coveringPros.length,
        sequential: true,
      })
    }

    return applyUserBusyFilter(
      { success: true, data: allSlots },
      options?.userId,
      date,
      options?.excludeAppointmentId,
      totalDur
    )
  } catch (error) {
    console.error('❌ Exception getting sequential any-specialist slots:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while checking availability',
    }
  }
}

/**
 * Get available time slots where ALL specified professionals are free
 * Used when multiple professionals are selected and all must be available
 * @param {Array} professionalIds - Array of professional IDs
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {number} durationMinutes - Duration in minutes
 * @returns {Promise<Object>} Available time slots where all professionals are free
 */
/**
 * Intersect multiple interval sets to find common free time
 * @param {Array} intervalSets - Array of interval arrays, each containing {start, end}
 * @returns {Array} Intersected intervals
 */
const intersectIntervals = (intervalSets) => {
  if (!intervalSets || intervalSets.length === 0) return []
  
  // If any professional has no free time, result is empty
  if (intervalSets.some(s => !s || !Array.isArray(s) || s.length === 0)) {
    return []
  }
  
  let result = intervalSets[0]
  for (let i = 1; i < intervalSets.length; i++) {
    const a = result
    const b = intervalSets[i]
    const next = []
    
    // If either array is empty, intersection is empty
    if (!a || a.length === 0 || !b || b.length === 0) {
      return []
    }
    
    a.forEach((iv1) => {
      b.forEach((iv2) => {
        const start = Math.max(iv1.start, iv2.start)
        const end = Math.min(iv1.end, iv2.end)
        if (end > start) next.push({ start, end })
      })
    })
    result = next
    if (result.length === 0) break
  }
  return result
}

export const getAvailableTimeSlotsForAllProfessionals = async (professionalIds, date, durationMinutes = 60, options = {}) => {
  try {
    if (isDemoMode()) {
      return buildDemoTimeSlots(date, durationMinutes, professionalIds || [])
    }

    if (!professionalIds || professionalIds.length === 0) {
      return {
        success: false,
        error: 'No professionals provided'
      }
    }

    // Fetch professional schedules
    const schedules = await fetchProfessionalSchedules(professionalIds)

    // Cross-user busy times (SECURITY DEFINER RPC) — direct segment SELECT is RLS-blind
    let appointmentsByProfessional = {}
    try {
      appointmentsByProfessional = await fetchBusyIntervalsByProfessionalRpc(professionalIds, date)
    } catch (busyErr) {
      console.error('Failed to load busy intervals via RPC:', busyErr)
      return {
        success: false,
        error: busyErr?.message || 'Could not load availability. Please try again.'
      }
    }

    // Check if ALL professionals are working on this date
    // If any professional is off, return no available slots
    const dateObj = parseLocalDateKey(date)
    const weekdayIdx = dateObj.getDay() // 0-6, Sunday=0
    const weekdayKey = String(weekdayIdx)
    
    for (const profId of professionalIds) {
      const schedule = schedules[profId]
      
      // If no schedule entry exists in database, professional is off
      if (!schedule || typeof schedule !== 'object' || Object.keys(schedule).length === 0) {
        console.log(`⚠️ Professional ${profId} has no schedule entry in database`)
        return {
          success: true,
          data: [] // No slots available - professional has no schedule
        }
      }
      
      // Check if schedule is inactive
      if (schedule.is_active === false || schedule.is_active === null) {
        console.log(`⚠️ Professional ${profId} is inactive (is_active = ${schedule.is_active})`)
        return {
          success: true,
          data: [] // No slots available - professional is off
        }
      }
      
      // Check if professional has working hours for this day
      const exceptions = schedule.exceptions || schedule.Exceptions || {}
      const rawDays = schedule.days || schedule.Days || {}
      
      // If days object is empty, professional is off
      if (!rawDays || typeof rawDays !== 'object' || Object.keys(rawDays).length === 0) {
        console.log(`⚠️ Professional ${profId} has no days configured in schedule`)
        return {
          success: true,
          data: [] // No slots available - professional has no working days
        }
      }
      
      // Check exception first (date-specific override)
      const exceptionDay = exceptions[date]
      const hasRawException = exceptionDay && typeof exceptionDay === 'object'
      
      // Treat explicit "off" exception (open === false and no custom hours) as closed day
      const isClosedException =
        hasRawException &&
        exceptionDay.open === false &&
        !exceptionDay.start_time &&
        !exceptionDay.close &&
        !exceptionDay.end_time
      
      // Check regular weekly schedule
      const weekdaySchedule = rawDays[weekdayKey]
      const hasWeekdaySchedule = weekdaySchedule && 
                                 typeof weekdaySchedule === 'object' && 
                                 (weekdaySchedule.open || weekdaySchedule.start_time) && 
                                 (weekdaySchedule.close || weekdaySchedule.end_time)
      
      // If explicit closed exception OR no schedule for this day, professional is off
      if (isClosedException || (!hasRawException && !hasWeekdaySchedule)) {
        console.log(`⚠️ Professional ${profId} has no working hours for ${date} (weekday ${weekdayIdx})`)
        console.log(`   Schedule data:`, { exceptions, rawDays, weekdayKey })
        return {
          success: true,
          data: [] // No slots available - professional is off on this day
        }
      }
    }

    // Compute free intervals for each professional
    // Only process professionals who passed the earlier schedule checks
    const freeIntervalsByProfessional = professionalIds.map(profId => {
      const schedule = schedules[profId]
      
      // Double-check: if no schedule, return empty
      if (!schedule || typeof schedule !== 'object' || Object.keys(schedule).length === 0) {
        console.log(`⚠️ Professional ${profId} has no schedule in freeIntervals computation - returning empty`)
        return []
      }
      
      const professionalAppointments = appointmentsByProfessional[profId] || []
      
      // Convert appointments/segments to busy intervals
      const busyIntervals = professionalAppointments.map(apt => ({
        start: apt.start_time?.substring(0, 5) || apt.start_time,
        end: apt.end_time?.substring(0, 5) || apt.end_time ||
             (apt.start_time ? minutesToTimeString(parseTimeToMinutes(apt.start_time?.substring(0, 5)) + (apt.duration_minutes || 60)) : null)
      })).filter(b => b.start && b.end)

      // Use strictMode=true for multi-professional bookings
      // This returns empty array if professional is off, instead of default hours
      return computeFreeIntervalsForProfessional(date, schedule, busyIntervals, true)
    })

    // If a professional has zero free time that day, sequential booking still can't use them
    if (freeIntervalsByProfessional.some(intervals => !intervals || intervals.length === 0)) {
      console.log('⚠️ At least one professional has no free intervals')
      return {
        success: true,
        data: []
      }
    }

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const selectedDateObj = parseLocalDateKey(date)
    selectedDateObj.setHours(0, 0, 0, 0)
    const isToday = today.getTime() === selectedDateObj.getTime()

    let minTimeMinutes = 0
    if (isToday) {
      const now = new Date()
      minTimeMinutes = (now.getHours() * 60) + now.getMinutes() + 30
    }

    const stepMinutes = 15
    const allSlots = []

    // Sequential plan: each service uses its own pro for its own window (A then B then C).
    const servicePlan = Array.isArray(options?.servicePlan) ? options.servicePlan : null
    const freeById = {}
    professionalIds.forEach((profId, idx) => {
      freeById[String(profId)] = freeIntervalsByProfessional[idx] || []
    })

    const covers = (intervals, start, end) =>
      (intervals || []).some((iv) => iv.start <= start && iv.end >= end)

    if (servicePlan && servicePlan.length > 0) {
      const totalDur = servicePlan.reduce(
        (sum, s) => sum + (Number(s.durationMinutes) || 0),
        0,
      )
      if (totalDur <= 0) {
        return { success: true, data: [] }
      }

      let windowStart = Infinity
      let windowEnd = -Infinity
      freeIntervalsByProfessional.forEach((intervals) => {
        (intervals || []).forEach((iv) => {
          if (iv.start < windowStart) windowStart = iv.start
          if (iv.end > windowEnd) windowEnd = iv.end
        })
      })
      if (!Number.isFinite(windowStart) || !Number.isFinite(windowEnd)) {
        return { success: true, data: [] }
      }

      for (let start = windowStart; start + totalDur <= windowEnd; start += stepMinutes) {
        if (isToday && start < minTimeMinutes) continue

        let cursor = start
        let ok = true
        for (const step of servicePlan) {
          const pid = String(step.professionalId || '')
          const dur = Number(step.durationMinutes) || 0
          if (!pid || dur <= 0 || !covers(freeById[pid], cursor, cursor + dur)) {
            ok = false
            break
          }
          cursor += dur
        }
        if (!ok) continue

        const timeString = `${minutesToTimeString(start)}:00`
        const endTimeString = `${minutesToTimeString(start + totalDur)}:00`
        const startDisplay = formatTimeForDisplay(timeString)
        const endDisplay = formatTimeForDisplay(endTimeString)

        allSlots.push({
          time: timeString,
          endTime: endTimeString,
          display: `${startDisplay} - ${endDisplay}`,
          displayStart: startDisplay,
          displayEnd: endDisplay,
          available: true,
          availableProfessionals: professionalIds,
          availableCount: professionalIds.length,
          sequential: true,
        })
      }
    } else {
      // Legacy: all professionals free for the full duration at once
      const commonFreeIntervals = intersectIntervals(freeIntervalsByProfessional)

      if (!commonFreeIntervals || commonFreeIntervals.length === 0) {
        console.log('⚠️ No common free intervals found for all professionals')
        return {
          success: true,
          data: []
        }
      }

      commonFreeIntervals.forEach(interval => {
        for (let totalMinutes = interval.start; totalMinutes + durationMinutes <= interval.end; totalMinutes += stepMinutes) {
          if (isToday && totalMinutes < minTimeMinutes) {
            continue
          }

          const timeString = `${minutesToTimeString(totalMinutes)}:00`
          const endTotalMinutes = totalMinutes + durationMinutes
          const endTimeString = `${minutesToTimeString(endTotalMinutes)}:00`

          const startDisplay = formatTimeForDisplay(timeString)
          const endDisplay = formatTimeForDisplay(endTimeString)

          allSlots.push({
            time: timeString,
            endTime: endTimeString,
            display: `${startDisplay} - ${endDisplay}`,
            displayStart: startDisplay,
            displayEnd: endDisplay,
            available: true,
            availableProfessionals: professionalIds,
            availableCount: professionalIds.length
          })
        }
      })
    }

    return applyUserBusyFilter(
      { success: true, data: allSlots },
      options?.userId,
      date,
      options?.excludeAppointmentId,
      durationMinutes
    )
  } catch (error) {
    console.error('❌ Exception getting available time slots for all professionals:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while checking availability'
    }
  }
}

/**
 * Get the last booking time for specific professionals
 * Used to suggest the same time when rebooking with the same professionals
 * @param {string} userId - User ID
 * @param {Array} professionalIds - Array of professional IDs to match
 * @returns {Promise<Object>} Last booking time and appointment details
 */
export const getLastBookingTimeForProfessionals = async (userId, professionalIds) => {
  try {
    if (!userId || !professionalIds || professionalIds.length === 0) {
      return {
        success: false,
        error: 'User ID and professional IDs are required'
      }
    }

    // Get user's completed appointments (most recent first)
    let lastBookingQuery = supabase
      .from('appointments')
      .select(`
        id,
        appointment_date,
        appointment_time,
        start_time,
        professional_id,
        services,
        created_at
      `)
      .eq('user_id', userId)
      .in('status', ['completed', 'confirmed']) // Include both completed and confirmed
    lastBookingQuery = applySalonFilter(lastBookingQuery)
    const { data: appointments, error } = await lastBookingQuery
      .order('created_at', { ascending: false })
      .limit(50) // Check last 50 appointments

    if (error) {
      console.error('❌ Error fetching previous appointments:', error)
      return {
        success: false,
        error: error.message
      }
    }

    if (!appointments || appointments.length === 0) {
      return {
        success: true,
        data: null // No previous bookings found
      }
    }

    // Find appointments that match the professional IDs
    // For multi-professional bookings, check if the appointment's professional_id matches any of the requested IDs
    // Or if the services array contains professionals matching the requested IDs
    const matchingAppointments = appointments.filter(apt => {
      // Check if appointment's professional_id matches any requested professional
      if (apt.professional_id && professionalIds.includes(apt.professional_id)) {
        return true
      }
      
      // Check if services array contains any of the requested professionals
      if (apt.services && Array.isArray(apt.services)) {
        const serviceProfessionalIds = apt.services
          .map(service => service.professional?.id || service.professional_id)
          .filter(Boolean)
        
        // Check if all requested professionals are in the service professionals
        const requestedSet = new Set(professionalIds.map(String))
        const serviceSet = new Set(serviceProfessionalIds.map(String))
        
        // Match if at least one professional matches, or if all requested professionals are present
        return professionalIds.some(id => serviceSet.has(String(id))) ||
               professionalIds.every(id => serviceSet.has(String(id)))
      }
      
      return false
    })

    if (matchingAppointments.length === 0) {
      return {
        success: true,
        data: null // No matching previous bookings
      }
    }

    // Get the most recent matching appointment
    const lastAppointment = matchingAppointments[0]

    return {
      success: true,
      data: {
        appointmentDate: lastAppointment.appointment_date,
        appointmentTime: lastAppointment.appointment_time || lastAppointment.start_time,
        startTime: lastAppointment.start_time || lastAppointment.appointment_time,
        professionalId: lastAppointment.professional_id,
        services: lastAppointment.services,
        appointmentId: lastAppointment.id
      }
    }
  } catch (error) {
    console.error('❌ Exception getting last booking time:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while fetching previous booking'
    }
  }
}

/**
 * Find the best available professional for a specific time slot
 * Returns the professional with maximum availability (most free time)
 * @param {Array} professionalIds - Array of professional IDs
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} startTime - Start time in HH:MM:SS format
 * @param {number} durationMinutes - Duration in minutes
 * @returns {Promise<Object>} Best professional ID and availability info
 */
export const findBestAvailableProfessional = async (
  professionalIds,
  date,
  startTime,
  durationMinutes = 60,
  serviceId = null,
  categoryId = null,
  categoryName = null
) => {
  try {
    if (!professionalIds || professionalIds.length === 0) {
      return {
        success: false,
        error: 'No professionals provided'
      }
    }

    // Campaign demo: pick the first candidate without touching schedules/segments
    if (isDemoMode()) {
      return {
        success: true,
        data: {
          professionalId: professionalIds[0],
          availableMinutes: 0,
          isAvailable: true,
        },
        professionalId: professionalIds[0],
      }
    }

    // Filter professionals if service/category context is provided.
    // Match by category_id OR category name (salon vs brand Featured/Hair Color UUIDs).
    let filteredProfessionalIds = professionalIds
    if ((serviceId || categoryId || categoryName) && professionalIds.length > 0) {
      const { data: professionals, error: profError } = await supabase
        .from('professionals')
        .select(`
          id,
          professional_specializations (
            category_id,
            excluded_service_ids,
            service_categories (
              id,
              name
            )
          )
        `)
        .in('id', professionalIds)
        .eq('is_active', true)

      if (!profError && professionals) {
        const { filterProfessionalsForService } = await import('./professionalService')
        const filtered = filterProfessionalsForService(
          professionals,
          categoryId,
          serviceId,
          categoryName
        )
        // Keep the original pool if name/id filter empties due to stale category UUIDs
        filteredProfessionalIds =
          filtered.length > 0 ? filtered.map((p) => p.id) : professionalIds
      }
    }

    // Fetch professional schedules
    const schedules = await fetchProfessionalSchedules(filteredProfessionalIds)

    // Cross-user busy data via SECURITY DEFINER RPC (direct segment SELECTs are
    // RLS-scoped to the caller's own bookings and wrongly treat other users'
    // "Any professional" appointments as free).
    let professionalSegments = {}
    try {
      professionalSegments = await fetchBusyIntervalsByProfessionalRpc(
        filteredProfessionalIds,
        date
      )
    } catch (busyErr) {
      console.error('❌ findBestAvailableProfessional busy RPC failed:', busyErr?.message || busyErr)
      return {
        success: false,
        error: 'Unable to check professional availability. Please try again.',
      }
    }

    // Parse start time to minutes for comparison
    const [startHH, startMM] = startTime.split(':').map(Number)
    const slotStartMinutes = startHH * 60 + (startMM || 0)
    const slotEndMinutes = slotStartMinutes + durationMinutes

    // Calculate availability for each professional
    const professionalAvailability = filteredProfessionalIds.map(profId => {
      const schedule = schedules[profId] || schedules[String(profId).toLowerCase()]
      const profSegments = professionalSegments[profId] || professionalSegments[String(profId)] || []
      
      // Check if professional is working on this date
      if (!schedule || typeof schedule !== 'object' || Object.keys(schedule).length === 0) {
        return {
          professionalId: profId,
          isAvailable: false,
          freeTimeMinutes: 0,
          appointmentCount: profSegments.length,
          conflictCount: 0,
          reason: 'No schedule'
        }
      }

      if (schedule.is_active === false || schedule.is_active === null) {
        return {
          professionalId: profId,
          isAvailable: false,
          freeTimeMinutes: 0,
          appointmentCount: profSegments.length,
          conflictCount: 0,
          reason: 'Inactive'
        }
      }

      // Convert segments to busy intervals
      const busyIntervals = profSegments.map(seg => ({
        start: String(seg.start_time || '').substring(0, 5) || seg.start_time,
        end: String(seg.end_time || '').substring(0, 5) || seg.end_time
      })).filter(b => b.start && b.end)

      // Calculate free intervals for this professional
      const freeIntervals = computeFreeIntervalsForProfessional(date, schedule, busyIntervals, true)
      
      // Calculate total free time in minutes
      const freeTimeMinutes = freeIntervals.reduce((sum, interval) => {
        return sum + (interval.end - interval.start)
      }, 0)

      // Check if this specific slot is available
      let isAvailable = false
      let conflictCount = 0

      // Check if the requested time slot is within any free interval
      for (const freeInterval of freeIntervals) {
        if (slotStartMinutes >= freeInterval.start && slotEndMinutes <= freeInterval.end) {
          isAvailable = true
          break
        }
      }

      // Count conflicts (overlapping segments)
      profSegments.forEach(seg => {
        const segStart = parseTimeToMinutes(String(seg.start_time || '').substring(0, 5) || seg.start_time)
        const segEnd = parseTimeToMinutes(String(seg.end_time || '').substring(0, 5) || seg.end_time)
        
        if (segStart != null && segEnd != null) {
          if (
            (slotStartMinutes >= segStart && slotStartMinutes < segEnd) ||
            (slotEndMinutes > segStart && slotEndMinutes <= segEnd) ||
            (slotStartMinutes <= segStart && slotEndMinutes >= segEnd)
          ) {
            conflictCount++
          }
        }
      })

      return {
        professionalId: profId,
        isAvailable,
        freeTimeMinutes,
        appointmentCount: profSegments.length,
        conflictCount
      }
    })

    // Filter to only available professionals (those who can take the requested slot)
    const availableProfessionals = professionalAvailability.filter(p => p.isAvailable)

    if (availableProfessionals.length === 0) {
      return {
        success: false,
        error: 'No professionals available for this time slot'
      }
    }

    // Among pros free for THIS slot: least booked first, then most remaining free time.
    // (Most-free-only kept re-assigning the same pro after short bookings.)
    availableProfessionals.sort((a, b) => {
      if (a.appointmentCount !== b.appointmentCount) {
        return a.appointmentCount - b.appointmentCount
      }
      if (b.freeTimeMinutes !== a.freeTimeMinutes) {
        return b.freeTimeMinutes - a.freeTimeMinutes
      }
      return a.conflictCount - b.conflictCount
    })

    // Return the professional with maximum availability (most free time)
    const bestProfessional = availableProfessionals[0]

    console.log(`✅ Selected professional ${bestProfessional.professionalId} with ${bestProfessional.freeTimeMinutes} minutes of free time (${bestProfessional.appointmentCount} bookings that day)`)

    return {
      success: true,
      data: {
        professionalId: bestProfessional.professionalId,
        appointmentCount: bestProfessional.appointmentCount,
        freeTimeMinutes: bestProfessional.freeTimeMinutes,
        availableCount: availableProfessionals.length
      }
    }
  } catch (error) {
    console.error('❌ Exception finding best available professional:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while finding available professional'
    }
  }
}

/**
 * Format time for display (HH:MM:SS -> "9:00 AM")
 * @param {string} timeString - Time in HH:MM:SS format
 * @returns {string} Formatted time string
 */
const formatTimeForDisplay = (timeString) => {
  const [hours, minutes] = timeString.split(':')
  const hour24 = parseInt(hours)
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
  const ampm = hour24 >= 12 ? 'PM' : 'AM'
  return `${hour12}:${minutes} ${ampm}`
}

/**
 * Fetch professional schedules from database
 * @param {Array} professionalIds - Array of professional IDs
 * @returns {Promise<Object>} Map of professional_id -> schedule object
 */
const fetchProfessionalSchedules = async (professionalIds) => {
  try {
    if (!professionalIds || professionalIds.length === 0) {
      return {}
    }

    const { data, error } = await supabase
      .from('professional_schedules')
      .select('professional_id, schedule')
      .in('professional_id', professionalIds)

    if (error) {
      console.error('❌ Error fetching professional schedules:', error)
      return {}
    }

    const scheduleMap = {}
    ;(data || []).forEach((row) => {
      let sched = row.schedule
      if (typeof sched === 'string') {
        try {
          sched = JSON.parse(sched)
        } catch (_) {
          sched = {}
        }
      }
      // Guard shape
      if (!sched || typeof sched !== 'object') sched = {}
      if (!sched.days || typeof sched.days !== 'object') sched.days = {}
      if (sched.is_active === undefined) {
        // If is_active is not set, default to true (assume active if schedule exists)
        sched.is_active = true
      }
      scheduleMap[row.professional_id] = sched
    })

    // Note: Professionals without entries in professional_schedules table
    // will not be in scheduleMap, which we'll check separately

    return scheduleMap
  } catch (error) {
    console.error('❌ Exception fetching professional schedules:', error)
    return {}
  }
}

/**
 * Parse time string to minutes from midnight
 * @param {string} timeString - Time in "HH:MM" or "HH:MM:SS" format
 * @returns {number|null} Minutes from midnight or null if invalid
 */
const parseTimeToMinutes = (timeString) => {
  if (!timeString) return null
  const parts = String(timeString).split(':')
  const hh = parseInt(parts[0], 10)
  const mm = parseInt(parts[1] || '0', 10)
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null
  return hh * 60 + mm
}

/**
 * Convert minutes from midnight to time string
 * @param {number} minutes - Minutes from midnight
 * @returns {string} Time in "HH:MM" format
 */
const minutesToTimeString = (minutes) => {
  const h = Math.floor(minutes / 60) % 24
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/**
 * Subtract intervals from base intervals
 * @param {Array} baseIntervals - Array of {start, end} in minutes
 * @param {Array} blocks - Array of {start, end} in minutes to subtract
 * @returns {Array} Remaining intervals
 */
const subtractIntervals = (baseIntervals, blocks) => {
  let result = [...baseIntervals]
  blocks.forEach((b) => {
    const next = []
    result.forEach((iv) => {
      if (b.end <= iv.start || b.start >= iv.end) {
        next.push(iv)
      } else {
        if (b.start > iv.start) {
          next.push({ start: iv.start, end: Math.max(iv.start, b.start) })
        }
        if (b.end < iv.end) {
          next.push({ start: Math.min(iv.end, b.end), end: iv.end })
        }
      }
    })
    result = next.filter((iv) => iv.end - iv.start > 0)
  })
  return result
}

/**
 * Compute free intervals for a professional based on schedule and busy times
 * @param {string} dateISO - Date in YYYY-MM-DD format
 * @param {Object} schedule - Schedule object with is_active, days, exceptions
 * @param {Array} busyIntervals - Array of {start: "HH:MM", end: "HH:MM"}
 * @returns {Array} Array of {start, end} in minutes representing free intervals
 */
const computeFreeIntervalsForProfessional = (dateISO, schedule, busyIntervals = [], strictMode = false) => {
  // If schedule is inactive or missing, professional is off – always return no free intervals
  if (!dateISO || !schedule || schedule.is_active === false) {
    return []
  }

  const date = parseLocalDateKey(dateISO)
  const weekdayIdx = date.getDay() // 0-6, Sunday=0
  const sundayKey = String(weekdayIdx)

  const rawDays = schedule.days || schedule.Days || {}
  const exceptions = schedule.exceptions || schedule.Exceptions || {}
  let day = (exceptions && exceptions[dateISO]) || null

  if (!day) {
    const sundayVal = rawDays[sundayKey]
    if (sundayVal && typeof sundayVal === 'object') {
      day = sundayVal
    } else {
      // No schedule for this day – professional is off
      return []
    }
  }
  
  // If this is an explicit "off" exception (open === false and no custom hours), treat as closed for all modes
  if (
    day &&
    day.open === false &&
    !day.start_time &&
    !day.close &&
    !day.end_time
  ) {
    return []
  }
  
  // Check if day has valid working hours
  const openLabel = day.open || day.start_time
  const closeLabel = day.close || day.end_time
  const openMin = parseTimeToMinutes(openLabel)
  const closeMin = parseTimeToMinutes(closeLabel)

  if (openMin == null || closeMin == null || closeMin <= openMin) {
    // Invalid schedule – treat as closed (no fallback default hours)
    return []
  }


  // Start with working hours as free interval
  let free = [{ start: openMin, end: closeMin }]

  // Subtract breaks
  const breaks = Array.isArray(day.breaks) ? day.breaks : []
  const breakBlocks = breaks
    .map((b) => ({
      start: parseTimeToMinutes(b[0]),
      end: parseTimeToMinutes(b[1])
    }))
    .filter((x) => x.start != null && x.end != null && x.end > x.start)
  free = subtractIntervals(free, breakBlocks)

  // Subtract busy intervals (from appointment_segments)
  const busyBlocks = (busyIntervals || [])
    .map((b) => ({
      start: parseTimeToMinutes(b.start),
      end: parseTimeToMinutes(b.end)
    }))
    .filter((x) => x.start != null && x.end != null && x.end > x.start)

  console.log(`🔍 Computing free intervals:`, {
    workingHours: { start: openMin, end: closeMin },
    breaks: breakBlocks.length,
    busyIntervals: busyBlocks.length,
    busyBlocks: busyBlocks
  })

  free = subtractIntervals(free, busyBlocks)
  
  console.log(`✅ Free intervals after subtracting busy times:`, free)
  
  return free
}

/**
 * Update appointment status
 * @param {string} appointmentId - Appointment ID
 * @param {string} status - New status
 * @returns {Promise<Object>} Updated appointment or error
 */
/**
 * Update appointment payment status and related fields
 * This triggers the database function to generate payment_reference_id
 *
 * IMPORTANT:
 * - Some environments enforce a UNIQUE constraint on payment_intent_id.
 * - If we hit a duplicate-key error on payment_intent_id, we still want to:
 *   - Mark payment_status = 'paid'
 *   - Preserve / generate payment_reference_id via the trigger
 */
export const updateAppointmentPayment = async (appointmentId, paymentData) => {
  try {
    const { payment_status, payment_method, payment_intent_id } = paymentData

    if (isDemoMode()) {
      return demoUpdateAppointment(appointmentId, {
        payment_status,
        payment_method,
        ...(payment_intent_id ? { payment_intent_id } : {}),
      })
    }

    const baseFields = {}
    if (payment_status) baseFields.payment_status = payment_status
    if (payment_method) baseFields.payment_method = payment_method

    const fullFields = {
      ...baseFields,
      ...(payment_intent_id ? { payment_intent_id } : {})
    }

    // First attempt: update including payment_intent_id (normal path)
    let { data, error } = await supabase
      .from('appointments')
      .update(fullFields)
      .eq('id', appointmentId)
      .select()
      .single()

    if (error) {
      const message = (error.message || '').toLowerCase()
      const details = (error.details || '').toLowerCase()

      const isDuplicateIntent =
        message.includes('duplicate key value') ||
        message.includes('unique constraint') ||
        details.includes('duplicate key value') ||
        details.includes('unique constraint') ||
        (error.code && String(error.code) === '23505')

      // If we hit a duplicate constraint on payment_intent_id, retry WITHOUT changing payment_intent_id
      if (isDuplicateIntent) {
        console.warn('⚠️ Duplicate payment_intent_id detected when updating appointment payment. Retrying without payment_intent_id field.', {
          appointmentId,
          error
        })

        // Retry with only status / method to ensure trigger still fires
        const retryResult = await supabase
          .from('appointments')
          .update(baseFields)
          .eq('id', appointmentId)
          .select()
          .single()

        if (retryResult.error) {
          console.error('❌ Retry updating appointment payment (without payment_intent_id) failed:', retryResult.error)
          return { success: false, error: retryResult.error.message || 'Failed to update appointment payment (retry)' }
        }

        console.log('✅ Appointment payment updated successfully on retry (without payment_intent_id):', retryResult.data.id)
        return { success: true, data: retryResult.data }
      }

      console.error('❌ Error updating appointment payment:', error)
      return { success: false, error: error.message }
    }

    console.log('✅ Appointment payment updated successfully:', data.id)
    return { success: true, data }
  } catch (error) {
    console.error('❌ Exception updating appointment payment:', error)
    return { success: false, error: error.message || 'Failed to update appointment payment' }
  }
}

export const updateAppointmentStatus = async (appointmentId, status) => {
  try {
    if (isDemoMode()) {
      if (String(status || '').toLowerCase() === 'cancelled') {
        return demoCancelAppointment(appointmentId)
      }
      return demoUpdateAppointment(appointmentId, { status })
    }

    const { data, error } = await supabase
      .from('appointments')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', appointmentId)
      .select()
      .single()

    if (error) {
      console.error('❌ Error updating appointment status:', error)
      return {
        success: false,
        error: error.message || 'Failed to update appointment status'
      }
    }

    // If appointment was cancelled, delete its segments so it no longer blocks availability.
    if (String(status || '').toLowerCase() === 'cancelled') {
      try {
        const del = await supabase
          .from('appointment_segments')
          .delete()
          .eq('appointment_id', appointmentId)
        if (del.error) {
          console.warn('⚠️ Error deleting appointment segments for cancelled appointment:', del.error)
        }
      } catch (segError) {
        console.warn('⚠️ Exception deleting appointment segments for cancelled appointment:', segError)
      }
    }

    return {
      success: true,
      data: data
    }
  } catch (error) {
    console.error('❌ Exception updating appointment status:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while updating appointment'
    }
  }
}

/**
 * Cancel an appointment
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise<Object>} Result
 */
export const cancelAppointment = async (appointmentId) => {
  return updateAppointmentStatus(appointmentId, 'cancelled')
}

/**
 * Cancel every appointment row sharing a group_booking_id (whole multi-day deal).
 * Optional notes / payment_status are applied only to primaryAppointmentId (payment day).
 */
export const cancelAppointmentsByGroupBookingId = async (
  groupBookingId,
  { primaryAppointmentId = null, notes = null, paymentStatus = null } = {}
) => {
  try {
    if (!groupBookingId) {
      return { success: false, error: 'Missing group_booking_id' }
    }

    if (isDemoMode()) {
      const all = demoGetAppointments(DEMO_USER_ID) || []
      const ids = all
        .filter((a) => a.group_booking_id === groupBookingId)
        .map((a) => a.id)
      for (const id of ids) {
        demoCancelAppointment(id)
      }
      return { success: true, cancelledIds: ids }
    }

    const { data: rows, error: fetchError } = await supabase
      .from('appointments')
      .select('id')
      .eq('group_booking_id', groupBookingId)

    if (fetchError) {
      return { success: false, error: fetchError.message || 'Failed to load deal appointments' }
    }

    const ids = (rows || []).map((r) => r.id).filter(Boolean)
    if (ids.length === 0) {
      return { success: false, error: 'No appointments found for this deal' }
    }

    const { error: updateError } = await supabase
      .from('appointments')
      .update({ status: 'cancelled', updated_at: new Date().toISOString() })
      .in('id', ids)

    if (updateError) {
      return { success: false, error: updateError.message || 'Failed to cancel deal appointments' }
    }

    if (notes != null || paymentStatus != null) {
      const patch = { updated_at: new Date().toISOString() }
      if (notes != null) patch.notes = notes
      if (paymentStatus != null) patch.payment_status = paymentStatus
      // Apply reschedule/cancel notes to every day so cancelled tab shows the tag
      const { error: notesError } = await supabase
        .from('appointments')
        .update(patch)
        .in('id', ids)
      if (notesError) {
        console.warn('⚠️ Failed to update deal appointment notes/payment:', notesError)
        // Fallback: still try primary if provided
        if (primaryAppointmentId) {
          await supabase.from('appointments').update(patch).eq('id', primaryAppointmentId)
        }
      }
    }

    try {
      const del = await supabase.from('appointment_segments').delete().in('appointment_id', ids)
      if (del.error) {
        console.warn('⚠️ Error deleting segments for cancelled deal:', del.error)
      }
    } catch (segError) {
      console.warn('⚠️ Exception deleting segments for cancelled deal:', segError)
    }

    return { success: true, cancelledIds: ids }
  } catch (error) {
    console.error('❌ Exception cancelling deal group:', error)
    return { success: false, error: error.message || 'Failed to cancel deal' }
  }
}

/**
 * Get appointment by ID
 * @param {string} appointmentId - Appointment ID
 * @returns {Promise<Object>} Appointment or error
 */
export const getAppointmentById = async (appointmentId) => {
  try {
    const { data, error } = await supabase
      .from('appointments')
      .select(`
        *,
        professional:professionals(id, name, role, image_url),
        salon:salons(id, name)
      `)
      .eq('id', appointmentId)
      .single()

    if (error) {
      console.error('❌ Error fetching appointment:', error)
      return {
        success: false,
        error: error.message || 'Failed to fetch appointment'
      }
    }

    return {
      success: true,
      data: data
    }
  } catch (error) {
    console.error('❌ Exception fetching appointment:', error)
    return {
      success: false,
      error: 'An unexpected error occurred while fetching appointment'
    }
  }
}
